import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Content from "../..//layout/content/Content";
import Head from "../../layout/head/Head";
import {
  Card,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
  Badge,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../components/Component";
import {
  Table,
  Collapse as AntCollapse,
  TableColumnsType,
  Timeline,
  Popover,
  Empty,
  Flex,
  Spin,
  Tag,
  Modal,
  Descriptions,
} from "antd";
import {
  PaperClipOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownOutlined,
  RightOutlined,
  EditOutlined,
  CopyOutlined,
  DeleteOutlined,
  FileTextOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { WorkOrderData, WorkOrderLine, WorkOrderLineSource } from "./types";
import { getWorkOrderById } from "./mockData/viewWorkOrderData";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { formatDecimal } from "../../utils/Utils";

const { Panel } = AntCollapse;

const WorkOrderView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [workOrder, setWorkOrder] = useState<WorkOrderData | null>(null);
  const [loading, setLoading] = useState(true);
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);
  const [sm, setSm] = useState(false);
  const [disableApprove, setDisableApprove] = useState(false);
  const [disableReject, setDisableReject] = useState(false);
  const [shareOptionsToggle, setShareOptionsToggle] = useState(false);

  // Mock current user permissions
  const [allowedActions] = useState({
    view: 1,
    edit: 1,
    approve: 1,
    reject: 1,
  });

  const [currentUserId] = useState(1); // Mock current user ID
  const [approverIds] = useState([1, 2, 3]); // Mock approver IDs

  useEffect(() => {
    if (id) {
      loadWorkOrderData(id);
    }
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, [id]);

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  const loadWorkOrderData = async (workOrderId: string) => {
    try {
      setLoading(true);
      const data: any = getWorkOrderById(workOrderId);

      if (data) {
        setWorkOrder(data);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Work Order not found",
        });
      }
    } catch (error) {
      console.error("Error loading work order:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load work order data",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (workOrder) {
      navigate(`/work-order-creation/?edit=${workOrder.id}`);
    }
  };

  const handleDuplicate = () => {
    if (workOrder) {
      navigate(`/work-order-creation/?duplicate=${workOrder.id}`);
    }
  };

  const handleDelete = () => {
    if (!workOrder) return;

    Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        // Mock delete functionality
        Swal.fire("Deleted!", "Work Order has been deleted.", "success").then(
          () => {
            navigate("/work-orders/view-work-orders");
          }
        );
      }
    });
  };

  const handleApprove = async () => {
    try {
      setDisableApprove(true);
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Mock approve functionality
      const updatedWorkOrder: any = { ...workOrder!, status: "Approved" };
      setWorkOrder(updatedWorkOrder);

      Swal.fire(
        "Approved!",
        "Work Order has been approved successfully",
        "success"
      ).then(() => {
        navigate("/work-orders/view-work-orders");
      });
    } catch (error) {
      console.error(error);
      Swal.fire("Error!", "Failed to approve Work Order", "error");
    } finally {
      setDisableApprove(false);
    }
  };

  const handleReject = async (rejectedReason: string) => {
    try {
      setDisableReject(true);
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Mock reject functionality
      const updatedWorkOrder: any = {
        ...workOrder!,
        status: "Rejected",
        rejectionComments: rejectedReason,
      };
      setWorkOrder(updatedWorkOrder);

      Swal.fire("Rejected!", "Work Order has been rejected", "success").then(
        () => {
          navigate("/work-orders/view-work-orders");
        }
      );
    } catch (error) {
      console.error(error);
      Swal.fire("Error!", "Failed to reject Work Order", "error");
    } finally {
      setDisableReject(false);
    }
  };

  const showRejectModal = () => {
    Swal.fire({
      title: "Reject Work Order",
      html: `
        <textarea 
          id="rejection-reason" 
          placeholder="Enter rejection reason (required)"
          style="width: 100%; height: 100px; margin-top: 10px; padding: 8px; border: 1px solid #ddd; border-radius: 4px;"
        ></textarea>
      `,
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Reject",
      cancelButtonText: "Cancel",
      preConfirm: () => {
        const reason = (
          document.getElementById("rejection-reason") as HTMLTextAreaElement
        )?.value;
        if (!reason.trim()) {
          Swal.showValidationMessage("Rejection reason is required");
          return false;
        }
        return reason;
      },
    }).then((result: any) => {
      if (result.isConfirmed) {
        handleReject(result.value);
      }
    });
  };

  const getStatusColor = (status: string): string => {
    const statusColors: { [key: string]: string } = {
      Draft: "secondary",
      "Pending Approval": "warning",
      Approved: "success",
      Rejected: "danger",
      Released: "info",
      "Contractor Confirmed": "primary",
      "In Execution": "info",
      "On Hold": "warning",
      Completed: "success",
      Closed: "secondary",
      Cancelled: "danger",
    };
    return statusColors[status] || "secondary";
  };

  const getSourceColor = (source: WorkOrderLineSource) => {
    const colors = {
      [WorkOrderLineSource.ProjectManagement]: "blue",
      [WorkOrderLineSource.WorkBOQ]: "green",
      [WorkOrderLineSource.NewWorkLine]: "orange",
    };
    return colors[source] || "default";
  };

  // Work Order Lines Table Columns
  const workOrderLinesColumns: TableColumnsType<any> = [
    {
      title: "#",
      key: "#",
      width: 50,
      fixed: "left",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Line #",
      dataIndex: "lineNumber",
      key: "lineNumber",
      width: 80,
      fixed: "left",
    },
    {
      title: "Task Title",
      dataIndex: "taskTitle",
      key: "taskTitle",
      width: 200,
      render: (text: string, record: WorkOrderLine) => (
        <div>
          <div className="fw-bold">{text}</div>
          {record.taskDescription && (
            <small className="text-muted">{record.taskDescription}</small>
          )}
        </div>
      ),
    },
    {
      title: "Category",
      dataIndex: "workCategory",
      key: "workCategory",
      width: 120,
      render: (text: string, record: WorkOrderLine) => (
        <div>
          <div>{text}</div>
          {record.subCategory && (
            <small className="text-muted">{record.subCategory}</small>
          )}
        </div>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
      render: (quantity: number, record: WorkOrderLine) =>
        `${formatDecimal(quantity)} ${record?.uom}`,
    },
    {
      title: "UOM",
      dataIndex: "uom",
      key: "uom",
      width: 80,
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      width: 120,
      render: (rate: number) => `₹${rate?.toLocaleString()}`,
    },
    {
      title: "Tax",
      key: "tax",
      width: 100,
      render: (_: any, record: WorkOrderLine) => (
        <div>
          {record.taxType && (
            <div className="small">
              {record.taxType}: {record.taxPercentage}%
            </div>
          )}
          <div className="fw-bold">₹{record.taxAmount?.toLocaleString()}</div>
        </div>
      ),
    },
    {
      title: "Total Amount",
      key: "totalAmount",
      width: 140,
      render: (_: any, record: WorkOrderLine) =>
        `₹${record.totalAmount?.toLocaleString()}`,
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      width: 140,
      render: (source: string) => (
        <Tag color={getSourceColor(source as WorkOrderLineSource)}>
          {source}
        </Tag>
      ),
    },
  ];

  const getStatusMapper = (status: string) => {
    const statusMap: { [key: string]: { title: string; color: string } } = {
      Draft: { title: "Draft", color: "secondary" },
      "Pending Approval": { title: "Pending Approval", color: "warning" },
      Approved: { title: "Approved", color: "success" },
      Rejected: { title: "Rejected", color: "danger" },
      Released: { title: "Released", color: "info" },
      "Contractor Confirmed": {
        title: "Contractor Confirmed",
        color: "primary",
      },
      "In Execution": { title: "In Execution", color: "info" },
      "On Hold": { title: "On Hold", color: "warning" },
      Completed: { title: "Completed", color: "success" },
      Closed: { title: "Closed", color: "secondary" },
      Cancelled: { title: "Cancelled", color: "danger" },
    };
    return statusMap[status] || { title: status, color: "secondary" };
  };

  if (loading) {
    return (
      <>
        <Head title="Work Order Details" />
        <Content>
          <Flex align="center" gap="middle" className="pageLoader">
            <Spin size="large" className="m-auto" />
          </Flex>
        </Content>
      </>
    );
  }

  if (!workOrder) {
    return (
      <>
        <Head title="Work Order Not Found" />
        <Content>
          <div className="text-center py-5">
            <Empty description="Work Order not found" />
          </div>
        </Content>
      </>
    );
  }

  const statusInfo = getStatusMapper(workOrder.status);

  // Calculate totals
  const subtotal =
    workOrder.workOrderLines?.reduce(
      (sum, line) => sum + line.quantity * line.rate,
      0
    ) || 0;
  const taxAmount =
    workOrder.workOrderLines?.reduce(
      (sum, line) => sum + (line.taxAmount || 0),
      0
    ) || 0;
  const grossTotal = subtotal + taxAmount;

  return (
    <>
      <Head title={`Work Order - ${workOrder.workOrderNumber}`} />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            <div className="card-inner card-inner-lg">
              {sm && mobileView && (
                <div
                  className="toggle-overlay"
                  onClick={() => setSm(!sm)}
                ></div>
              )}
              <BlockHead size="lg">
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h4">
                      <span>
                        {workOrder.workOrderNumber} | {statusInfo.title}
                      </span>
                    </BlockTitle>
                  </BlockHeadContent>
                  <div className="d-flex">
                    {/* Action buttons based on status */}
                    {workOrder.status === "Draft" && (
                      <div className="actions clearfix">
                        <ul
                          className="m-auto d-flex align-items-center justify-content-center p-0"
                          style={{ flexDirection: "row-reverse" }}
                        >
                          <li className="pt-0 pb-0">
                            <Button color="primary" onClick={handleEdit}>
                              <EditOutlined /> Edit
                            </Button>
                          </li>
                          <li className="pt-0 pb-0">
                            <Button color="secondary" onClick={handleDuplicate}>
                              <CopyOutlined /> Duplicate
                            </Button>
                          </li>
                          <li className="pt-0 pb-0">
                            <Button color="danger" onClick={handleDelete}>
                              <DeleteOutlined /> Delete
                            </Button>
                          </li>
                        </ul>
                      </div>
                    )}

                    {workOrder.status === "Pending Approval" &&
                      allowedActions?.approve === 1 &&
                      approverIds.includes(currentUserId) && (
                        <div className="actions clearfix">
                          <ul
                            className="m-auto d-flex align-items-center justify-content-center p-0"
                            style={{ flexDirection: "row-reverse" }}
                          >
                            <li className="pt-0 pb-0">
                              {disableApprove ? (
                                <Button color="success" type="button">
                                  <Spinner size="sm" color="light" />
                                </Button>
                              ) : (
                                <Button
                                  color="success"
                                  type="button"
                                  onClick={() => {
                                    Swal.fire({
                                      title: "Are you sure?",
                                      text: "Do you want to approve this Work Order?",
                                      icon: "warning",
                                      showCancelButton: true,
                                      confirmButtonText: "Yes, Approve it!",
                                      cancelButtonText: "No, keep it",
                                    }).then((result) => {
                                      if (result.isConfirmed) {
                                        handleApprove();
                                      }
                                    });
                                  }}
                                >
                                  Approve
                                </Button>
                              )}
                            </li>
                            <li className="pt-0 pb-0">
                              {disableReject ? (
                                <Button color="danger" type="button">
                                  <Spinner size="sm" color="light" />
                                </Button>
                              ) : (
                                <Button
                                  color="danger"
                                  onClick={showRejectModal}
                                  type="button"
                                  className="btn-dim btn btn-secondary"
                                >
                                  Reject
                                </Button>
                              )}
                            </li>
                          </ul>
                        </div>
                      )}

                    <div className="actions clearfix">
                      <ul
                        className="m-auto d-flex align-items-center justify-content-center p-0"
                        style={{ flexDirection: "row-reverse" }}
                      >
                        <li className="pt-0 pb-0">
                          <Dropdown
                            isOpen={shareOptionsToggle}
                            toggle={() => {
                              setShareOptionsToggle(!shareOptionsToggle);
                            }}
                          >
                            <DropdownToggle className="btn btn-white btn-outline-light">
                              <Icon name="share"></Icon>
                              <span>Share</span>
                            </DropdownToggle>
                            <DropdownMenu>
                              <DropdownItem onClick={() => {}}>
                                <Icon name="mail"></Icon>
                                <span>Send Mail</span>
                              </DropdownItem>
                              <DropdownItem onClick={() => {}}>
                                <Icon name="download"></Icon>
                                <span>Export PDF</span>
                              </DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                        </li>

                        <li className="pt-0 pb-0">
                          <Button
                            className="toggle"
                            color="primary"
                            onClick={() => {
                              navigate("/view-work-orders");
                            }}
                          >
                            <Icon name="arrow-left" />
                            <span>Back</span>
                          </Button>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <BlockHeadContent className="align-self-start d-lg-none">
                    <Button
                      className={`toggle btn btn-icon btn-trigger mt-n1 ${
                        sm ? "active" : ""
                      }`}
                      onClick={() => setSm(!sm)}
                    >
                      <Icon name="menu-alt-r" />
                    </Button>
                  </BlockHeadContent>
                </BlockBetween>
              </BlockHead>

              <Alert color={"danger"} isOpen={workOrder.status === "Rejected"}>
                <strong>Rejected: </strong>
                {(workOrder as any).rejectionComments || "No reason provided"}
              </Alert>

              <div className="content clearfix">
                <AntCollapse defaultActiveKey={["1", "2", "3", "4"]}>
                  {/* Section A: General Information */}
                  <Panel
                    header={
                      <div className="d-flex align-items-center justify-content-between">
                        <>📋 General Information</>
                      </div>
                    }
                    key="1"
                  >
                    <Descriptions bordered size="small" column={2}>
                      <Descriptions.Item label="Work Order Number">
                        {workOrder.workOrderNumber}
                      </Descriptions.Item>
                      <Descriptions.Item label="Contract Date">
                        {workOrder.contractDate
                          ? dayjs(workOrder.contractDate).format("DD/MM/YYYY")
                          : "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Work Order Title" span={2}>
                        {workOrder.workOrderTitle}
                      </Descriptions.Item>
                      <Descriptions.Item label="Site">
                        {workOrder.siteName}
                      </Descriptions.Item>
                      <Descriptions.Item label="Project">
                        {workOrder.projectName || "Not selected"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Contract Type">
                        {workOrder.contractType}
                      </Descriptions.Item>
                      <Descriptions.Item label="Contract With">
                        {workOrder.contractWith}
                      </Descriptions.Item>
                      <Descriptions.Item label="Contractor">
                        {workOrder.contractorName}
                      </Descriptions.Item>
                      <Descriptions.Item label="Work Order Owner">
                        {workOrder.workOrderOwner}
                      </Descriptions.Item>
                      <Descriptions.Item label="Planned Start">
                        {workOrder.plannedStartDate
                          ? dayjs(workOrder.plannedStartDate).format(
                              "DD/MM/YYYY"
                            )
                          : "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Planned End">
                        {workOrder.plannedEndDate
                          ? dayjs(workOrder.plannedEndDate).format("DD/MM/YYYY")
                          : "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Approval Required">
                        <Tag
                          color={
                            workOrder.approvalRequired ? "orange" : "green"
                          }
                        >
                          {workOrder.approvalRequired ? "Yes" : "No"}
                        </Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="Tax Enabled">
                        <Tag color={workOrder.enableTax ? "blue" : "default"}>
                          {workOrder.enableTax ? "Yes" : "No"}
                        </Tag>
                      </Descriptions.Item>
                    </Descriptions>

                    {workOrder.workOrderDescription && (
                      <div className="mt-3">
                        <strong>Description:</strong>
                        <p className="mt-1 text-muted">
                          {workOrder.workOrderDescription}
                        </p>
                      </div>
                    )}
                  </Panel>

                  {/* Section B: Work Order Lines */}
                  <Panel
                    header={`📋 Work Order Lines (${
                      workOrder.workOrderLines?.length || 0
                    })`}
                    key="2"
                  >
                    <div className="mb-3">
                      <Tag color="blue">
                        {workOrder.workOrderLines?.length || 0} line(s)
                      </Tag>
                    </div>

                    <div className="table-responsive poTable">
                      <div
                        style={{
                          width: screenSize - 170 + "px",
                          margin: "auto",
                        }}
                      >
                        {!workOrder.workOrderLines ||
                        workOrder.workOrderLines.length === 0 ? (
                          <div className="text-center py-5">
                            <Empty description="No work order lines found" />
                          </div>
                        ) : (
                          <Table
                            className="customTable"
                            columns={workOrderLinesColumns}
                            dataSource={workOrder.workOrderLines}
                            scroll={{ x: 1200 }}
                            bordered
                            size="small"
                            pagination={false}
                            rowKey="id"
                          />
                        )}
                      </div>

                      {workOrder.workOrderLines?.length > 0 && (
                        <div className="mt-3 p-3 bg-light rounded">
                          <Row>
                            <Col lg="8">
                              <div className="d-flex justify-content-between">
                                <span>Subtotal:</span>
                                <strong>₹{subtotal.toLocaleString()}</strong>
                              </div>
                              {workOrder.enableTax && (
                                <div className="d-flex justify-content-between">
                                  <span>Tax Amount:</span>
                                  <strong>₹{taxAmount.toLocaleString()}</strong>
                                </div>
                              )}
                              <hr className="my-2" />
                              <div className="d-flex justify-content-between">
                                <span>
                                  <strong>Gross Total:</strong>
                                </span>
                                <strong className="text-success">
                                  ₹{grossTotal.toLocaleString()}
                                </strong>
                              </div>
                            </Col>
                          </Row>
                        </div>
                      )}
                    </div>
                  </Panel>

                  {/* Section C: General Conditions */}
                  <Panel
                    header={`📋 General Conditions (${
                      workOrder.generalConditions?.length || 0
                    })`}
                    key="3"
                  >
                    <div className="mb-3">
                      <Tag color="blue">
                        {workOrder.generalConditions?.length || 0} condition(s)
                        selected
                      </Tag>
                    </div>

                    {workOrder.generalConditions &&
                    workOrder.generalConditions.length > 0 ? (
                      <div>
                        {workOrder.generalConditions
                          .sort((a, b) => a.order - b.order)
                          .map((condition: any, index) => {
                            const hasContent =
                              condition.conditionContent &&
                              condition.conditionContent.trim() !== "";
                            const hasAttachments =
                              condition.attachments &&
                              condition.attachments.length > 0;

                            return (
                              <div
                                key={condition.id}
                                className="mb-4 p-3 border rounded"
                              >
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                  <h6 className="mb-0">
                                    <FileTextOutlined className="me-2" />
                                    {condition.order}. {condition.conditionName}
                                  </h6>
                                  <div>
                                    {hasContent && (
                                      <Tag color="green">Text</Tag>
                                    )}
                                    {hasAttachments && (
                                      <Tag color="blue">Documents</Tag>
                                    )}
                                  </div>
                                </div>

                                {/* Text Content */}
                                {hasContent && (
                                  <div className="mb-3">
                                    <div className="p-2 bg-light rounded">
                                      <small className="text-primary fw-medium">
                                        Content:
                                      </small>
                                      <p
                                        className="mb-0 mt-1 small"
                                        style={{ whiteSpace: "pre-wrap" }}
                                      >
                                        {condition.conditionContent}
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {/* Attachments */}
                                {hasAttachments && (
                                  <div className="mt-2">
                                    <small className="text-primary fw-medium d-block mb-2">
                                      <PaperClipOutlined className="me-1" />
                                      Documents ({condition.attachments.length}
                                      ):
                                    </small>
                                    <div className="row">
                                      {condition.attachments.map(
                                        (attachment: any, idx: any) => (
                                          <div
                                            key={attachment.id}
                                            className="col-md-6 mb-2"
                                          >
                                            <div className="border rounded p-2 d-flex justify-content-between align-items-center">
                                              <div className="d-flex align-items-center">
                                                <FileTextOutlined className="text-muted me-2" />
                                                <div>
                                                  <div
                                                    className="small fw-medium"
                                                    title={attachment.fileName}
                                                  >
                                                    {attachment.fileName
                                                      .length > 25
                                                      ? `${attachment.fileName.substring(
                                                          0,
                                                          25
                                                        )}...`
                                                      : attachment.fileName}
                                                  </div>
                                                  <small className="text-muted">
                                                    {(
                                                      attachment.fileSize /
                                                      1024 /
                                                      1024
                                                    ).toFixed(2)}
                                                    MB
                                                  </small>
                                                </div>
                                              </div>
                                              <div>
                                                <small className="text-muted">
                                                  {dayjs(
                                                    attachment.uploadDate
                                                  ).format("DD/MM/YY")}
                                                </small>
                                              </div>
                                            </div>
                                          </div>
                                        )
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    ) : (
                      <div className="text-center text-muted py-3">
                        No general conditions selected
                      </div>
                    )}
                  </Panel>

                  {/* Section D: Payment Conditions */}
                  <Panel header="📋 Payment Conditions" key="4">
                    <div className="mb-3">
                      <Tag color="green">
                        <DollarOutlined className="me-1" />
                        Contract Value: ₹{grossTotal.toLocaleString()}
                      </Tag>
                    </div>

                    {workOrder.paymentConditions ? (
                      <Descriptions bordered size="small" column={1}>
                        <Descriptions.Item label="Mobilization Advance">
                          {workOrder.paymentConditions.mobilizationAdvance
                            .isPercentOfContract
                            ? `${workOrder.paymentConditions.mobilizationAdvance.percentageOfContract}% of contract value`
                            : `₹${workOrder.paymentConditions.mobilizationAdvance.advanceValue?.toLocaleString()}`}
                          {workOrder.paymentConditions.mobilizationAdvance
                            .interestCharged &&
                            ` (Interest: ${workOrder.paymentConditions.mobilizationAdvance.interestRate}%)`}
                          <br />
                          <small className="text-muted">
                            Recovery:{" "}
                            {
                              workOrder.paymentConditions.mobilizationAdvance
                                .recoveryType
                            }
                          </small>
                        </Descriptions.Item>

                        <Descriptions.Item label="Retention Amount">
                          {workOrder.paymentConditions.retentionAmount
                            .isPercentOfRABill
                            ? `${workOrder.paymentConditions.retentionAmount.percentageOfRABill}% of RA Bill`
                            : `₹${workOrder.paymentConditions.retentionAmount.retentionValue?.toLocaleString()}`}
                          <br />
                          <small className="text-muted">
                            DLP:{" "}
                            {workOrder.paymentConditions.retentionAmount
                              .dlpAmount || 1}{" "}
                            {workOrder.paymentConditions.retentionAmount
                              .dlpUnit || "years"}
                            <br />
                            Release Date:{" "}
                            {workOrder.paymentConditions.retentionAmount
                              .releaseDate
                              ? dayjs(
                                  workOrder.paymentConditions.retentionAmount
                                    .releaseDate
                                ).format("DD/MM/YYYY")
                              : "Not set"}
                          </small>
                        </Descriptions.Item>

                        <Descriptions.Item label="On Hold Amount">
                          {workOrder.paymentConditions.onHoldAmount
                            .isPercentOfContract
                            ? `${workOrder.paymentConditions.onHoldAmount.percentageOfContract}% of contract value`
                            : `₹${workOrder.paymentConditions.onHoldAmount.holdAmountValue?.toLocaleString()}`}
                          <br />
                          <small className="text-muted">
                            DLP:{" "}
                            {workOrder.paymentConditions.onHoldAmount
                              .dlpAmount || 1}{" "}
                            {workOrder.paymentConditions.onHoldAmount.dlpUnit ||
                              "years"}
                            <br />
                            Release Date:{" "}
                            {workOrder.paymentConditions.onHoldAmount
                              .releaseDate
                              ? dayjs(
                                  workOrder.paymentConditions.onHoldAmount
                                    .releaseDate
                                ).format("DD/MM/YYYY")
                              : "Not set"}
                          </small>
                        </Descriptions.Item>

                        <Descriptions.Item label="Pay on Work Completion">
                          <Tag
                            color={
                              workOrder.paymentConditions.payOnWorkCompletion
                                ? "orange"
                                : "green"
                            }
                          >
                            {workOrder.paymentConditions.payOnWorkCompletion
                              ? "Yes"
                              : "No"}
                          </Tag>
                        </Descriptions.Item>
                      </Descriptions>
                    ) : (
                      <div className="text-center text-muted py-3">
                        No payment conditions configured
                      </div>
                    )}
                  </Panel>

                  {/* Status History Panel */}
                  <Panel header="📊 Status History" key="5">
                    <Timeline>
                      <Timeline.Item color="blue">
                        <p>
                          <strong>Work Order Created</strong>
                        </p>
                        <p className="text-muted">
                          {dayjs(workOrder.createdAt).format(
                            "DD MMM YYYY HH:mm"
                          )}{" "}
                          by {workOrder.createdBy}
                        </p>
                      </Timeline.Item>

                      {workOrder.status === "Pending Approval" && (
                        <Timeline.Item color="orange">
                          <p>
                            <strong>Submitted for Approval</strong>
                          </p>
                          <p className="text-muted">
                            {dayjs(
                              workOrder.updatedAt || workOrder.createdAt
                            ).format("DD MMM YYYY HH:mm")}
                          </p>
                        </Timeline.Item>
                      )}

                      {workOrder.status === "Approved" && (
                        <Timeline.Item color="green">
                          <p>
                            <strong>Approved</strong>
                          </p>
                          <p className="text-muted">
                            {dayjs(
                              workOrder.updatedAt || workOrder.createdAt
                            ).format("DD MMM YYYY HH:mm")}
                          </p>
                          {(workOrder as any).approverComments && (
                            <p className="text-muted">
                              Comments: {(workOrder as any).approverComments}
                            </p>
                          )}
                        </Timeline.Item>
                      )}

                      {workOrder.status === "Rejected" && (
                        <Timeline.Item color="red">
                          <p>
                            <strong>Rejected</strong>
                          </p>
                          <p className="text-muted">
                            {dayjs(
                              workOrder.updatedAt || workOrder.createdAt
                            ).format("DD MMM YYYY HH:mm")}
                          </p>
                          {(workOrder as any).rejectionComments && (
                            <p className="text-muted">
                              Reason: {(workOrder as any).rejectionComments}
                            </p>
                          )}
                        </Timeline.Item>
                      )}
                    </Timeline>
                  </Panel>
                </AntCollapse>
              </div>
            </div>
          </div>
        </Card>
      </Content>
    </>
  );
};

export default WorkOrderView;
